import { Card, CardContent } from "@/components/ui/library/card";
import SearchInput from "@/components/ui/searchInput";
import { useAppDispatch } from "@/stores/hook";
import {
  setServiceDetailId,
  setIsServiceDetails,
} from "@/features/videoProduction/stores/videoProductionFunctionalSlice";
import { Briefcase, Layers, FileText, IndianRupee } from "lucide-react";
import { AdvancedSkeleton } from "@/components/feedback/loading/Skeletons";
import { ErrorBlock } from "@/components/feedback/error/AlertError";
import { EmptyState } from "@/components/feedback/empty/EmptyState";
import useServices from "@/features/videoProduction/api/useServices";
import { useState } from "react";

const iconClass = "size-4 shrink-0 text-[#267355]";

export default function Service() {
  const dispatch = useAppDispatch();
  const { services, isLoading, error, refetch } = useServices();
  const [search, setSearch] = useState("");

  const filtered = (services ?? []).filter((s: any) =>
    (s.name ?? "").toLowerCase().includes(search.toLowerCase())
  );

  const handleServiceClick = (id: number) => {
    dispatch(setServiceDetailId(id));
    dispatch(setIsServiceDetails(true));
  };

  if (isLoading) {
    return (
      <div className="pt-2 pb-4">
        <AdvancedSkeleton variant="grid" count={4} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <ErrorBlock
          message={(error as Error)?.message ?? "Failed to load services"}
          retry={refetch}
        />
      </div>
    );
  }

  if (!services?.length) {
    return (
      <div className="flex items-center justify-center py-6 w-full">
        <EmptyState
          title="No Services Found"
          description="Create a new service to get started."
        />
      </div>
    );
  }

  return (
    <Card className="border-black-1 rounded-[16px] shadow-none">
      <CardContent className="px-4">
        <div className="flex flex-col gap-4">
          <SearchInput
            placeholder="Search services..."
            isKbd={false}
            onChange={(e: any) => setSearch(e.target.value)}
          />
          <div>
            <span className="text-b2-mid">Services</span>
            <span className="text-b2-mid text-[#697571]">
              {" "}
              ({filtered.length})
            </span>
          </div>
          <div className="flex flex-col gap-3">
            {filtered.map((service: any) => {
              const variantCount = Array.isArray(service.variants)
                ? service.variants.length
                : 0;

              const prices = (service.variants ?? [])
                .map((v: any) => v.price)
                .filter((p: any) => p != null && !isNaN(p));
              const minPrice = prices.length ? Math.min(...prices) : null;
              const maxPrice = prices.length ? Math.max(...prices) : null;

              const priceLabel =
                minPrice != null
                  ? minPrice === maxPrice
                    ? `${minPrice.toLocaleString()}`
                    : `${minPrice.toLocaleString()} – ${maxPrice?.toLocaleString() ?? ""}`
                  : null;

              return (
                <div
                  key={service.id}
                  onClick={() => handleServiceClick(service.id)}
                  className="w-full rounded-[20px] border cursor-pointer transition-shadow hover:shadow-md overflow-hidden p-4 flex flex-col gap-2.5"
                  style={{
                    borderColor: "var(--color-green-2, #d6ebe3)",
                    background:
                      "linear-gradient(152deg, #EDFAF5 -11.48%, #FFF 52.35%)",
                    boxShadow: "0 4px 8px 0 rgba(30, 48, 42, 0.03)",
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="size-9 shrink-0 rounded-full bg-[rgba(38,115,85,0.1)] flex items-center justify-center">
                      <Briefcase className="size-4 text-[#051912]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#051912] truncate">
                        {service.name ?? "Unnamed Service"}
                      </p>
                      {service.code && (
                        <p className="text-xs text-[#697571] truncate">
                          {service.code}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 pl-0">
                    {service.description && (
                      <div className="flex items-start gap-2 min-w-0">
                        <FileText className={iconClass} />
                        <span className="text-xs text-[#697571] line-clamp-2 pt-0.5">
                          {service.description}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 min-w-0">
                      <Layers className={iconClass} />
                      <span className="text-xs text-[#697571]">
                        {variantCount} variant{variantCount !== 1 ? "s" : ""}
                      </span>
                    </div>
                    {priceLabel && (
                      <div className="flex items-center gap-2 min-w-0">
                        <IndianRupee className={iconClass} />
                        <span className="text-xs text-[#697571]">
                          {priceLabel}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
